import { supabase } from './supabase';

interface SamsungHealthData {
  dataType: 'heart_rate' | 'stress' | 'hrv' | 'sleep' | 'spo2' | 'activity' | 'recovery' | 'energy';
  value: any;
  timestamp: Date;
  deviceId?: string;
}

// Since we don't have actual Samsung Health SDK access,
// we'll simulate the data for development purposes
function generateMockData(dataType: SamsungHealthData['dataType']): any {
  switch (dataType) {
    case 'heart_rate':
      return { bpm: 60 + Math.floor(Math.random() * 40) };
    case 'stress':
      return { level: Math.floor(Math.random() * 100) };
    case 'hrv':
      return { ms: 20 + Math.floor(Math.random() * 60) };
    case 'sleep':
      return {
        duration: 6 + Math.random() * 4,
        quality: Math.floor(Math.random() * 100),
        stages: ['light', 'deep', 'rem']
      };
    case 'spo2':
      return { percentage: 95 + Math.floor(Math.random() * 5) };
    case 'activity':
      return {
        steps: Math.floor(Math.random() * 10000),
        calories: Math.floor(Math.random() * 500)
      };
    case 'recovery':
      return { score: Math.floor(Math.random() * 100) };
    case 'energy':
      return { level: Math.floor(Math.random() * 100) };
    default:
      return {};
  }
}

export async function checkSamsungHealthConnection(userId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('samsung_health_tokens')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (error && error.code !== 'PGRST116') throw error;
    return !!data;
  } catch (error) {
    console.error('Error checking Samsung Health connection:', error);
    return false;
  }
}

export async function syncSamsungHealthData(userId: string): Promise<void> {
  try {
    // Get user settings
    const { data: settings, error: settingsError } = await supabase
      .from('samsung_health_settings')
      .select('metrics_enabled')
      .eq('user_id', userId)
      .maybeSingle();

    if (settingsError) throw settingsError;
    if (!settings?.metrics_enabled) return;

    // Generate mock data for each enabled metric
    const data: SamsungHealthData[] = [];
    const now = new Date();

    for (const [metric, enabled] of Object.entries(settings.metrics_enabled)) {
      if (!enabled) continue;

      data.push({
        dataType: metric as SamsungHealthData['dataType'],
        value: generateMockData(metric as SamsungHealthData['dataType']),
        timestamp: now,
        deviceId: 'SAMSUNG-GALAXY-FIT3-MOCK'
      });
    }

    // Save data to Supabase
    if (data.length > 0) {
      const { error } = await supabase
        .from('samsung_health_data')
        .insert(data.map(d => ({
          user_id: userId,
          data_type: d.dataType,
          value: d.value,
          timestamp: d.timestamp,
          device_id: d.deviceId
        })));

      if (error) throw error;
    }
  } catch (error) {
    console.error('Error syncing Samsung Health data:', error);
    throw error;
  }
}

let syncInterval: NodeJS.Timeout | null = null;

export async function startSamsungHealthSync(userId: string): Promise<void> {
  try {
    // Stop any existing sync
    if (syncInterval) {
      clearInterval(syncInterval);
    }

    // Get sync interval from settings
    const { data: settings, error } = await supabase
      .from('samsung_health_settings')
      .select('sync_interval')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) throw error;
    if (!settings) return;

    // Initial sync
    await syncSamsungHealthData(userId);

    // Set up periodic sync
    syncInterval = setInterval(
      () => syncSamsungHealthData(userId),
      (settings.sync_interval || 5) * 60 * 1000 // Convert minutes to milliseconds
    );
  } catch (error) {
    console.error('Error starting Samsung Health sync:', error);
    throw error;
  }
}

export async function stopSamsungHealthSync(): Promise<void> {
  if (syncInterval) {
    clearInterval(syncInterval);
    syncInterval = null;
  }
}